import {
  CFormCheck,
  CModal,
  CModalBody,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import { useMutation, useQueryClient } from "react-query";
import DeleteIcon from "../../components/icons/DeleteIcon";
import EditIcon from "../../components/icons/EditIcon";
import TranslateIcon from "../../components/icons/TranslateIcon";
import PromptModal from "../../components/modals/PromptModal";
import Pagination from "../../components/ui/Pagination";
import RenderTable from "../../components/ui/RenderTable";
import { DELETE_ACTIONS } from "../../config/constants";
import { colors } from "../../config/theme";
import {
  bulkDeleteReducer,
  bulkDeleteState,
} from "../../context/bulkDeleteReducer";
import { useAuth } from "../../hooks/useAuth";
import { useCookie } from "../../hooks/useCookie";
import { useServiceCategoriesQuery } from "../../hooks/ServiceCategories/userServiceCategories";
import { usePaginatedContent } from "../../hooks/usePaginatedContent";
import { useToast } from "../../hooks/useToast";
import { general } from "../../locales/general";
import apiService from "../../service/apiService";
import NewCategory from "./NewCategory";
import { controlStyle, tdStyle, thStyle } from "../../helpers/constants";
import { Chip, IconAction } from "../../helpers/general";
import { cardStyle } from "../Places/PlacesTable";

const PAGE_SIZE = 10;



/* ─────────────── Edit modal ─────────────── */

function EditCategoryModal({
  category,
  onClose,
}: {
  category: ServiceCategory;
  onClose: () => void;
}) {
  const { token } = useCookie("vToken");
  const { toastError, toastSuccess } = useToast();
  const queryClient = useQueryClient();
  const [label, setLabel] = useState(category.label ?? "");

  const update = useMutation((data: { label: string }) =>
    apiService.MakePutRequest(`service-categories/${category.id}`, data, token),
  );

  const save = () =>
    update.mutate(
      { label: label.trim() },
      {
        onError: () => toastError(general.fr.message.operationFailed),
        onSuccess() {
          queryClient.invalidateQueries("getAllServiceCategories");
          toastSuccess(general.fr.message.categoryUpdated);
          onClose();
        },
      },
    );

  const unchanged = label.trim() === category.label || label.trim() === "";

  return (
    <CModal visible onClose={onClose} alignment="center">
      <CModalBody>
        <h5 className="fs-5 mb-4 border-bottom pb-1">
          Mettre à jour la catégorie
        </h5>
        <div className="mb-3">
          <label htmlFor="category-label">Nom de la catégorie</label>
          <input
            id="category-label"
            type="text"
            className="custom-input w-100 mt-1"
            placeholder="Nom de la catégorie"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </div>
        <button
          type="button"
          onClick={save}
          disabled={update.isLoading || unchanged}
          className="d-flex align-items-center justify-content-center btn btn-danger shadow-primary w-100 mt-2 p-2 fw-bold text-white"
        >
          {update.isLoading ? (
            <>
              <CSpinner size="sm" /> {general.fr.wait}
            </>
          ) : (
            "Enregistrer"
          )}
        </button>
      </CModalBody>
    </CModal>
  );
}

/* ─────────────── Row ─────────────── */

interface RowProps {
  category: ServiceCategory;
  deleteList: React.Dispatch<BulkDeleteActions>;
  isAdmin: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

function CategoryRow({
  category,
  deleteList,
  isAdmin,
  onEdit,
  onDelete,
}: RowProps) {
  const [isChecked, setIsChecked] = useState(false);

  const toggle = useCallback(
    (checked: boolean) => {
      setIsChecked(checked);
      deleteList({
        type: checked ? DELETE_ACTIONS.ADD : DELETE_ACTIONS.REMOVE,
        id: category.id,
      });
    },
    [deleteList, category.id],
  );

  // A row that unmounts (page change, search) must not stay selected
  useEffect(
    () => () => deleteList({ type: DELETE_ACTIONS.REMOVE, id: category.id }),
    [deleteList, category.id],
  );

  return (
    <CTableRow
      style={{
        backgroundColor: isChecked ? "rgba(240,129,63,0.06)" : undefined,
        boxShadow: isChecked ? `inset 3px 0 0 ${colors.primary}` : undefined,
      }}
    >
      <CTableDataCell style={tdStyle}>
        <div className="d-flex align-items-center gap-3">
          <CFormCheck
            checked={isChecked}
            onChange={(e) => toggle(e.target.checked)}
          />
          <span className="fw-semibold">{category.label}</span>
        </div>
      </CTableDataCell>

      <CTableDataCell style={tdStyle}>
        <div className="d-flex align-items-center justify-content-end gap-2">
          <IconAction title="Modifier" onClick={onEdit}>
            <EditIcon width="16" height="16" fill={colors.secondary} />
          </IconAction>
          {isAdmin && (
            <IconAction
              title="Traduire"
              to="/service-category-translations/new"
              state={{ category }}
            >
              <TranslateIcon width="18" height="18" fill="#000" />
            </IconAction>
          )}
          <IconAction title="Supprimer" danger onClick={onDelete}>
            <DeleteIcon width="18" height="18" fill={colors.primary} />
          </IconAction>
        </div>
      </CTableDataCell>
    </CTableRow>
  );
}

/* ─────────────── Tab ─────────────── */

export default function CategoriesTab() {
  const { isAdmin } = useAuth();
  const { Cities, isLoading, deleteServiceCategory, mutating } =
    useServiceCategoriesQuery();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [addVisible, setAddVisible] = useState(false);
  const [toEdit, setToEdit] = useState<ServiceCategory | null>(null);
  const [toDelete, setToDelete] = useState<ServiceCategory | null>(null);

  const [selected, dispatch] = useReducer(bulkDeleteReducer, bulkDeleteState);
  const selectedCount = selected?.ids?.length ?? 0;

  // Search goes back to page 1
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const filtered = useMemo(
    () =>
      (Cities ?? []).filter((c: any) =>
        c.label?.toLowerCase()?.includes(searchQuery),
      ),
    [Cities, searchQuery],
  );

  // Derived value, no state or effect needed
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = usePaginatedContent(currentPage, PAGE_SIZE, filtered);

  const handleBulkDelete = () => {
    [...selected.ids].forEach((id: string) => {
      if (!id) return;
      deleteServiceCategory(id);
      dispatch({ type: DELETE_ACTIONS.REMOVE, id });
    });
  };

  const total = Cities?.length ?? 0;

  return (
    <div>
      {/* Count + add */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        {Cities && (
          <Chip tone="warning">
            {total} {total > 1 ? "Catégories" : "Catégorie"}
          </Chip>
        )}
        <button
          type="button"
          onClick={() => setAddVisible(true)}
          className="btn btn-primary text-white rounded-pill px-4 border-0"
        >
          + Ajouter une catégorie
        </button>
      </div>

      {/* Toolbar */}
      <div
        style={{ ...cardStyle, padding: "12px 16px" }}
        className="d-flex flex-wrap align-items-center gap-2 mb-3"
      >
        <div
          className="d-flex align-items-center gap-2 px-3 flex-grow-1"
          style={{ ...controlStyle, minWidth: 200, maxWidth: 360 }}
        >
          <MagnifyingGlassIcon color="gray" width={18} />
          <input
            type="text"
            placeholder="Rechercher une catégorie"
            className="border-0 w-100 bg-transparent"
            style={{ outline: "none", fontSize: 13 }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value.toLowerCase())}
          />
        </div>

        <button
          type="button"
          disabled={mutating || selectedCount === 0}
          onClick={handleBulkDelete}
          className="btn btn-outline-danger rounded-pill ms-auto d-flex align-items-center gap-2 px-3"
        >
          {mutating ? (
            <>
              <CSpinner size="sm" /> {general.fr.wait}
            </>
          ) : (
            <>
              <DeleteIcon fill={colors.primary} /> Supprimer
              {selectedCount > 0 && ` (${selectedCount})`}
            </>
          )}
        </button>
      </div>

      {/* Table */}
      <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
        <RenderTable
          loading={isLoading}
          render={() =>
            paginated?.length > 0 ? (
              <CTable responsive hover align="middle" className="mb-0">
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell style={thStyle}>
                      Nom de la catégorie
                    </CTableHeaderCell>
                    <CTableHeaderCell
                      style={{ ...thStyle, textAlign: "right" }}
                    >
                      Actions
                    </CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {(paginated as ServiceCategory[]).map((category) => (
                    <CategoryRow
                      key={category.id}
                      category={category}
                      deleteList={dispatch}
                      isAdmin={Boolean(isAdmin)}
                      onEdit={() => setToEdit(category)}
                      onDelete={() => setToDelete(category)}
                    />
                  ))}
                </CTableBody>
              </CTable>
            ) : (
              <p className="text-center text-muted py-5 mb-0">
                {general.fr.noData}
              </p>
            )
          }
        />
      </div>

      <Pagination
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        siblingCount={1}
        totalCount={totalPages}
        pageSize={PAGE_SIZE}
      />

      {/* One modal of each kind for the whole table */}
      <CModal
        alignment="center"
        visible={addVisible}
        onClose={() => setAddVisible(false)}
      >
        <NewCategory onAccept={() => {}} onClose={() => setAddVisible(false)} />
      </CModal>

      {toEdit && (
        <EditCategoryModal
          key={toEdit.id}
          category={toEdit}
          onClose={() => setToEdit(null)}
        />
      )}

      <PromptModal
        open={toDelete !== null}
        onAccept={() => {
          if (toDelete) deleteServiceCategory(toDelete.id);
          setToDelete(null);
        }}
        onClose={() => setToDelete(null)}
      />
    </div>
  );
}
